/**
 * BehaviorSummaryCard Component - Phase 2C-4
 *
 * Summary statistics card for behavior analytics.
 * BSL Compliant: Factual numbers only, no interpretive labels.
 */

import { Card, CardHeader, CardTitle, CardContent, LoadingSpinner } from '../common';
import { BehaviorSummary } from '../../api';

interface BehaviorSummaryCardProps {
  summary: BehaviorSummary | null;
  loading?: boolean;
  error?: string | null;
}

export function BehaviorSummaryCard({
  summary,
  loading,
  error,
}: BehaviorSummaryCardProps): JSX.Element {
  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-slate-400">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!summary) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-slate-400">Select a pool to view analytics</p>
        </CardContent>
      </Card>
    );
  }

  const stats = [
    { label: 'Total Events', value: summary.total_events.toLocaleString() },
    { label: 'Unique Users', value: summary.unique_users.toLocaleString() },
    { label: 'Avg Decision Time', value: `${summary.avg_decision_time.toFixed(1)}s` },
    { label: 'Avg Stake %', value: `${summary.avg_stake_percentage.toFixed(1)}%` },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pool Behavioral Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center p-4 bg-slate-800/50 rounded-lg">
              <p className="text-2xl font-mono font-semibold text-slate-100">
                {stat.value}
              </p>
              <p className="text-sm text-slate-400 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}